import {useCallback, useEffect, useState} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useLocation} from 'react-router-dom';
import {GET_RECOMMENDATIONS} from '../resolvers';
import {errorMessage, isGqlError} from '../constants';

/** Guard against scanning an unbounded list if an id no longer exists. */
const MAX_PAGES_SCANNED = 20;
const SCAN_PAGE_SIZE = 100;

/**
 * Resolves the parent recommendation for the workspace.
 *
 * There is no admin get-recommendation-by-id query that covers drafts, and the
 * picks query returns picks and reports rather than the parent metadata. So the
 * list row travels in route state on navigation, and a hard refresh falls back
 * to paging the list until the id turns up.
 */
export const useRecommendationRow = recommendationId => {
  const {state} = useLocation();
  const carried =
    state?.recommendation?.id === recommendationId
      ? state.recommendation
      : null;

  const [recommendation, setRecommendation] = useState(carried);
  const [loading, setLoading] = useState(!carried);
  const [error, setError] = useState(null);
  const [fetchList] = useLazyQuery(GET_RECOMMENDATIONS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const findRow = useCallback(async () => {
    let page = 1;
    let pages = 1;
    do {
      // eslint-disable-next-line no-await-in-loop
      const {data} = await fetchList({
        variables: {page, limit: SCAN_PAGE_SIZE, fetchUnpublished: true},
      });
      const result = data?.getInstrumentRecommendations;
      if (isGqlError(result)) throw new Error(errorMessage(result));
      const match = result?.data?.find(item => item.id === recommendationId);
      if (match) return match;
      pages = result?.pagination?.pages || 1;
      page += 1;
    } while (page <= pages && page <= MAX_PAGES_SCANNED);
    return null;
  }, [fetchList, recommendationId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const match = await findRow();
      if (!match) setError('This recommendation could not be found.');
      else {
        setRecommendation(match);
        setError(null);
      }
      return match;
    } catch (lookupError) {
      setError(lookupError.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [findRow]);

  useEffect(() => {
    if (carried) return;
    refresh();
  }, [carried, refresh]);

  return {recommendation, loading, error, refresh};
};

export default useRecommendationRow;
